import { useParams } from "react-router-dom";
import { useQueryServiceByServiceId } from "../api/queries";
import { getStorageToken } from "../utils";
import { AppBar, Body, LoadingPage, NotFound } from "../components";
import { PaymentProvider } from "../context/paymentContext";
import { PaymentPage } from "../shared";
import { Box } from "@mui/material";
import { useTheme } from "../hooks";
import { usePortal } from "../context/portalContext";
import { CorporateService } from "../models/Service";
import { resolveFlowTemplate } from "../constants/flowTemplates";
import { FLOW_TEMPLATE_COMPONENTS } from "../components/templates";

const Service = () => {
  const token = getStorageToken();
  const { id = "" } = useParams();
  const { theme } = useTheme();
  // Scope and branding are resolved once for the session. This page used to
  // fetch the theme and then the group theme on every mount -- two round trips
  // per journey step -- and mirror the result into context. That is the
  // re-derivation this plan removes.
  const { scope, state } = usePortal();

  const corporateId = scope?.entity_id ? String(scope.entity_id) : "";

  const service = useQueryServiceByServiceId({
    corporateId,
    serviceId: id,
    enabled: !!token && !!id && !!corporateId,
    authorisation: token || "",
  });

  if (state === "resolving") return <LoadingPage />;

  // The same three terminal states the home page shows, for the same reason:
  // the branding shell stands rather than the page going blank. Decided
  // BEFORE the service read is consulted: with no school resolved that read
  // is never sent, and a read that is never sent reports itself as loading
  // for good -- the payer would sit on a spinner instead of this answer.
  if (state === "not-found" || state === "expired" || state === "unavailable") {
    return (
      <Body>
        <AppBar theme={theme} />
        <NotFound variant={state} />
      </Body>
    );
  }

  if (service?.isLoading) return <LoadingPage />;

  const corporateService = service?.data as CorporateService;

  // The journey is chosen from the declarative registry rather than by a branch
  // here, so adding a service type is a registry row plus a template module.
  const flow = resolveFlowTemplate({
    service_type: corporateService?.service_type,
    variant: corporateService?.variant ?? null,
    is_searchable: corporateService?.is_searchable,
  });

  const Template = flow.matched
    ? FLOW_TEMPLATE_COMPONENTS[flow.templateId]
    : undefined;

  return (
    <PaymentProvider>
      <Body>
        <AppBar theme={theme} />
        <Box my={2}>
          {Template ? (
            <Template
              theme={theme}
              corporateId={corporateId}
              service={corporateService}
            />
          ) : (
            // ⚠ CORRECTED 2026-09-10. This read "every identifier the registry
            // declares but 16-22 does not own a module for -- the three
            // SCHOOLFEE journeys". Since 12-09 every identifier HAS a module,
            // so this branch is now reached only by a triple the registry
            // matches nothing for (D-02's NO_FLOW_TEMPLATE) -- an unknown
            // service type, or the deliberately absent SCHOOLFEE·REGISTRATION·
            // searchable row. TODO-245 replaces the deprecated page here with a
            // visible refusal; it is not this plan's change.
            <PaymentPage
              theme={theme}
              corporateId={corporateId}
              service={corporateService}
            />
          )}
        </Box>
      </Body>
    </PaymentProvider>
  );
};

export default Service;
